import { Box, Typography, useMediaQuery } from "@mui/material";

import journeyPath from "../../../assets/Images/Temples/journey-path.svg";
import journeyPathVertical from "../../../assets/Images/Temples/m-journey-path.svg";
import { SectionHeading, TEMPLE_COLORS } from "../shared";

const StepNode = ({ icon, number, isFinal }) => (
  <Box sx={{ position: "relative", width: 92, height: 92, flexShrink: 0 }}>
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "46px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(isFinal
          ? {
              background: "linear-gradient(180deg, #F04951 0%, #C3303A 100%)",
              filter: "drop-shadow(0px 10px 12px rgba(240, 73, 81, 0.3))",
            }
          : {
              background: "#FFFFFF",
              border: "1.2px solid rgba(240, 73, 81, 0.35)",
              filter: "drop-shadow(0px 10px 12px rgba(0, 0, 0, 0.05))",
            }),
      }}
    >
      <Box component="img" src={icon} alt="" sx={{ maxWidth: 36, maxHeight: 36 }} />
    </Box>
    <Box
      sx={{
        position: "absolute",
        left: 66,
        top: -4,
        width: 30,
        height: 30,
        borderRadius: "15px",
        border: "2px solid #FFFFFF",
        background: isFinal ? "#FFFFFF" : TEMPLE_COLORS.red,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "11px", lineHeight: "14px", color: isFinal ? TEMPLE_COLORS.red : "#FFFFFF" }}>
        {String(number).padStart(2, "0")}
      </Typography>
    </Box>
  </Box>
);

// Mobile: 60px node with the step number badge.
const MobileStepNode = ({ icon, number, isFinal }) => (
  <Box sx={{ position: "relative", width: 60, height: 60, flexShrink: 0 }}>
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "30px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isFinal ? TEMPLE_COLORS.red : "#FFFFFF",
        border: isFinal ? "none" : "1.2px solid rgba(240, 73, 81, 0.35)",
      }}
    >
      <Box component="img" src={icon} alt="" sx={{ maxWidth: 26, maxHeight: 26 }} />
    </Box>
    <Box
      sx={{
        position: "absolute",
        left: 40,
        top: -4,
        width: 24,
        height: 24,
        boxSizing: "border-box",
        borderRadius: "12px",
        border: "2px solid #FFFFFF",
        background: isFinal ? "#FFFFFF" : TEMPLE_COLORS.red,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "10px", lineHeight: "12px", color: isFinal ? TEMPLE_COLORS.red : "#FFFFFF" }}>
        {String(number).padStart(2, "0")}
      </Typography>
    </Box>
  </Box>
);

const MobileHowItWorks = ({ content }) => (
  <Box component="section" sx={{ background: "#FFFFFF", px: "20px", py: "76px", display: "flex", flexDirection: "column", gap: "32px" }}>
    <SectionHeading tag={content.tag} title={content.title} description={content.description} isMobile />
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {content.steps.map((step, index) => {
        const isFinal = index === content.steps.length - 1;
        return (
          <Box key={step.title} sx={{ position: "relative", display: "flex", gap: "16px", alignItems: "flex-start", pb: isFinal ? 0 : "24px" }}>
            <MobileStepNode icon={step.icon} number={index + 1} isFinal={isFinal} />
            <Box sx={{ flex: 1, minWidth: 0, pt: "8px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography component="h3" sx={{ fontWeight: 700, fontSize: "16px", lineHeight: "22px", color: TEMPLE_COLORS.ink }}>
                {step.title}
              </Typography>
              <Typography sx={{ fontSize: "13px", lineHeight: "20px", color: TEMPLE_COLORS.muted }}>{step.description}</Typography>
            </Box>
            {!isFinal && (
              <Box
                component="img"
                src={journeyPathVertical}
                alt=""
                // Runs on under the next node (which covers it), whatever the text height.
                sx={{ position: "absolute", left: 29, top: 64, width: 2, height: "calc(100% - 50px)" }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  </Box>
);

const HowItWorksSection = ({ content }) => {
  const isTablet = useMediaQuery("(max-width: 1099px)");
  const isMobile = useMediaQuery("(max-width: 599px)");

  if (isMobile) return <MobileHowItWorks content={content} />;

  return (
    <Box component="section" sx={{ background: "#FFFFFF", px: isMobile ? "16px" : "40px", py: isMobile ? "56px" : "100px" }}>
      <SectionHeading tag={content.tag} title={content.title} description={content.description} isMobile={isMobile} />

      <Box
        sx={{
          position: "relative",
          mt: "56px",
          mx: "auto",
          maxWidth: 1360,
          display: "grid",
          gridTemplateColumns: isTablet ? (isMobile ? "1fr" : "repeat(2, 1fr)") : "repeat(4, 1fr)",
          rowGap: "40px",
        }}
      >
        {!isTablet && (
          <Box
            component="img"
            src={journeyPath}
            alt=""
            sx={{ position: "absolute", left: "16.47%", top: 44, width: "67.06%", height: 4 }}
          />
        )}
        {content.steps.map((step, index) => (
          <Box
            key={step.title}
            sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", px: "20px", textAlign: "center" }}
          >
            <StepNode icon={step.icon} number={index + 1} isFinal={index === content.steps.length - 1} />
            <Typography component="h3" sx={{ mt: "36px", fontWeight: 700, fontSize: "20px", lineHeight: "26px", color: TEMPLE_COLORS.ink }}>
              {step.title}
            </Typography>
            <Typography sx={{ mt: "14px", fontSize: "15px", lineHeight: "24px", color: TEMPLE_COLORS.muted }}>
              {step.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HowItWorksSection;
